/**
 * 会话存储管理工具
 * 基于源网站的会话制存储模式，支持多次测评并存
 * 支持未完成会话的保存、恢复和管理
 * 
 * 动物性格测试版本 - 简化的会话结构
 */

const STORAGE_KEY = 'animal_test_sessions_v1'
const PROGRESS_STORAGE_KEY = 'animal_test_progress_v1'
const MAX_SESSIONS = 50
const SESSION_EXPIRE_DAYS = 30

/**
 * 获取所有会话数据
 */
export function getAllSessions() {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    if (!data) {
      return { sessions: [] }
    }
    
    const parsed = JSON.parse(data)
    return {
      sessions: parsed.sessions || [],
      lastCleanup: parsed.lastCleanup || null
    }
  } catch (e) {
    console.error('Error loading sessions:', e)
    return { sessions: [] }
  }
}

/**
 * 保存所有会话数据
 */
export function saveAllSessions(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.error('Error saving sessions:', e)
    if (e instanceof Error && e.name === 'QuotaExceededError') {
      // 存储空间不足，清理旧数据
      console.log('Storage quota exceeded, cleaning old sessions...')
      const cleanedData = {
        ...data,
        sessions: data.sessions.slice(0, 10) // 只保留最近10条
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedData))
    }
  }
}

/**
 * 根据ID获取会话
 */
export function getSessionById(sessionId) {
  const { sessions } = getAllSessions()
  return sessions.find(session => session.id === sessionId)
}

/**
 * 创建新会话（动物测试版本）
 */
export function createSession(sessionData) {
  const data = getAllSessions()
  const session = {
    id: `animal_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    startTime: new Date().toISOString(),
    completed: false,
    responses: {}, // 动物测试使用对象 { "1": "A", "2": "B", ... }
    demographics: {
      nickname: '',
      gender: ''
    },
    currentQuestion: 1, // 当前题目序号（1-60）
    ...sessionData
  }
  
  data.sessions.unshift(session)
  
  // 限制会话数量
  if (data.sessions.length > MAX_SESSIONS) {
    data.sessions = data.sessions.slice(0, MAX_SESSIONS)
  }
  
  saveAllSessions(data)
  return session
}

/**
 * 更新会话
 */
export function updateSession(sessionId, updates) {
  const data = getAllSessions()
  const sessionIndex = data.sessions.findIndex(s => s.id === sessionId)
  
  if (sessionIndex === -1) {
    console.error('Session not found:', sessionId)
    return null
  }
  
  data.sessions[sessionIndex] = {
    ...data.sessions[sessionIndex],
    ...updates
  }
  
  saveAllSessions(data)
  return data.sessions[sessionIndex]
}

/**
 * 完成会话（添加结果）
 */
export function completeSession(sessionId, results) {
  return updateSession(sessionId, {
    results,
    endTime: new Date().toISOString(),
    completed: true
  })
}

/**
 * 删除会话
 */
export function deleteSession(sessionId) {
  const data = getAllSessions()
  data.sessions = data.sessions.filter(s => s.id !== sessionId)
  saveAllSessions(data)
}

/**
 * 清理旧会话（保留最近N条）
 */
export function cleanupOldSessions(keepCount = 30) {
  const data = getAllSessions()
  const now = new Date()
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  
  // 按时间排序，保留最近的
  data.sessions = data.sessions
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, keepCount)
    .filter(session => {
      // 删除超过一个月的会话
      return new Date(session.timestamp) > oneMonthAgo
    })
  
  data.lastCleanup = now.toISOString()
  saveAllSessions(data)
  
  return data.sessions.length
}

/**
 * 获取已完成会话列表（用于历史记录）
 */
export function getCompletedSessions() {
  const { sessions } = getAllSessions()
  return sessions.filter(session => session.completed && session.results)
}

/**
 * 迁移旧RPI数据（动物测试版本不需要此功能）
 * 保留函数但不执行任何操作，避免报错
 */
export function migrateOldData() {
  // 动物测试不需要迁移RPI数据
  // 如需清理旧数据，请手动在设置页面操作
}

/**
 * 清理所有会话数据（动物测试版本）
 */
export function clearAllSessions() {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(PROGRESS_STORAGE_KEY)
  // 可选：清理旧的RPI数据
  // localStorage.removeItem('rpi_sessions')
  // localStorage.removeItem('rpi_assessment_progress')
}

/**
 * 获取未完成的会话列表
 */
export function getIncompleteSessions() {
  const { sessions } = getAllSessions()
  return sessions.filter(session => !session.completed)
}

/**
 * 获取未完成的会话（动物测试版本）
 * 只有在题库页面选择了1道及以上题目才算未提交
 */
export function getIncompleteSession() {
  const incompleteSessions = getIncompleteSessions()
  return incompleteSessions.find(session => {
    // 检查是否有有效的答题进度
    if (session.responses && typeof session.responses === 'object') {
      // responses是对象格式，检查是否有有效答案
      const answeredCount = Object.keys(session.responses).length
      return answeredCount >= 1
    }
    
    return false
  })
}

/**
 * 保存会话进度
 */
export function saveSessionProgress(sessionId, progressData) {
  const session = getSessionById(sessionId)
  if (!session) {
    console.error('Session not found:', sessionId)
    return false
  }

  const updatedSession = {
    ...session,
    ...progressData,
    lastUpdated: new Date().toISOString()
  }

  updateSession(sessionId, updatedSession)
  
  // 同时保存到进度存储（用于页面刷新恢复）
  try {
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify({
      sessionId,
      ...progressData,
      timestamp: new Date().toISOString()
    }))
  } catch (e) {
    console.error('Error saving progress to localStorage:', e)
  }

  return true
}

/**
 * 恢复会话进度（动物测试版本）
 */
export function restoreSessionProgress() {
  try {
    const progressData = localStorage.getItem(PROGRESS_STORAGE_KEY)
    if (!progressData) return null

    const progress = JSON.parse(progressData)
    const session = getSessionById(progress.sessionId)
    
    if (!session) {
      // 会话不存在，清理进度数据
      localStorage.removeItem(PROGRESS_STORAGE_KEY)
      return null
    }

    return {
      session,
      progress: {
        responses: progress.responses || {},
        demographics: progress.demographics || {},
        currentQuestion: progress.currentQuestion || 1
      }
    }
  } catch (e) {
    console.error('Error restoring session progress:', e)
    localStorage.removeItem(PROGRESS_STORAGE_KEY)
    return null
  }
}

/**
 * 清除会话进度
 */
export function clearSessionProgress() {
  localStorage.removeItem(PROGRESS_STORAGE_KEY)
}

/**
 * 检查是否有未完成的会话（动物测试版本）
 */
export function hasIncompleteSession() {
  const incompleteSessions = getIncompleteSessions()
  return incompleteSessions.some(session => {
    // 检查是否有有效答题进度
    if (session.responses && typeof session.responses === 'object') {
      return Object.keys(session.responses).length >= 1
    }
    return false
  })
}

/**
 * 获取会话进度信息（动物测试版本）
 */
export function getSessionProgressInfo(sessionId) {
  const session = getSessionById(sessionId)
  if (!session) return null

  // 动物测试固定60题
  const totalQuestions = 60
  
  // 计算已回答题目数量
  let answeredCount = 0
  if (session.responses && typeof session.responses === 'object') {
    answeredCount = Object.keys(session.responses).length
  }
  
  const progress = Math.round((answeredCount / totalQuestions) * 100)

  return {
    sessionId: session.id,
    answeredCount,
    totalQuestions,
    progress,
    startTime: session.startTime,
    lastUpdated: session.lastUpdated,
    currentQuestion: session.currentQuestion || 1,
    demographics: session.demographics || {}
  }
}

/**
 * 清理过期的会话
 */
export function cleanupExpiredSessions() {
  const data = getAllSessions()
  const now = new Date()
  const expireTime = new Date(now.getTime() - SESSION_EXPIRE_DAYS * 24 * 60 * 60 * 1000)

  const originalCount = data.sessions.length
  
  // 清理过期的未完成会话
  data.sessions = data.sessions.filter(session => {
    if (session.completed) return true // 保留已完成的会话
    
    const sessionTime = new Date(session.startTime || session.timestamp)
    return sessionTime > expireTime
  })

  // 按时间排序，保留最近的
  data.sessions = data.sessions
    .sort((a, b) => new Date(b.startTime || b.timestamp) - new Date(a.startTime || a.timestamp))
    .slice(0, MAX_SESSIONS)

  if (data.sessions.length !== originalCount) {
    data.lastCleanup = now.toISOString()
    saveAllSessions(data)
    console.log(`Cleaned up ${originalCount - data.sessions.length} expired sessions`)
  }

  return data.sessions.length
}

/**
 * 获取会话统计信息（动物测试版本）
 */
export function getSessionStats() {
  const { sessions } = getAllSessions()
  const completed = sessions.filter(s => s.completed)
  const incomplete = sessions.filter(s => !s.completed)
  
  return {
    total: sessions.length,
    completed: completed.length,
    incomplete: incomplete.length
  }
}
